import { defineStore } from "pinia";
import { ref, computed } from "vue";
import adminLeaveService from "@/services/hrm/adminLeaveService";
import { toast } from "@/utils/toast";
import { createScopedCache } from "./_cache";

const adminLeaveListCache = createScopedCache("dashboard:admin:leaves:list");
const adminLeaveStatsCache = createScopedCache("dashboard:admin:leaves:stats");
const adminLeaveChartCache = createScopedCache("dashboard:admin:leaves:chart");

const ADMIN_LEAVE_LIST_TTL = 60 * 1000;
const ADMIN_LEAVE_STATS_TTL = 60 * 1000;
const ADMIN_LEAVE_CHART_TTL = 90 * 1000;

export const useAdminLeaveStore = defineStore("adminLeaves", () => {
  const items = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });

  const stats = ref({
    total_pending: 0,
    total_approved: 0,
    total_declined: 0,
    days_booked: 0,
    total_requests: 0,
  });

  const filters = ref({
    q: "",
    date_from: "",
    date_to: "",
    status: "",
    type_id: "",
    per_page: 25,
    page: 1,
    sort: "recent",
  });

  const totalPages = computed(() => {
    const t = pagination.value.total || 0;
    const p = pagination.value.per_page || 25;
    return Math.max(1, Math.ceil(t / p));
  });

  // --------- CHART STATE (dynamic leave types) ----------
  const chartYear = ref(new Date().getFullYear());
  const chartMonths = ref([]); // ["Jan", "Feb", ...]
  const chartSeries = ref([]); // [{ name: 'Annual', data: [..] }, ...]

  const chartCategories = ref([
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ]);
  const chartYears = ref([]);

  // async function fetchChart(extra = {}) {
  //   try {
  //     const params = {
  //       year: chartYear.value,
  //       ...extra,
  //     };
  //     const { data } = await adminLeaveService.summary(params);
  //     chartYear.value = data.year || chartYear.value;
  //     chartMonths.value = data.months || [];
  //     chartSeries.value = data.series || [];
  //   } catch (e) {
  //     toast(
  //       e?.response?.data?.message || "Failed to load leave chart",
  //       "error"
  //     );
  //   }
  // }
  // ------------------------------------------------------

  async function fetchChart(extra = {}) {
    try {
      const params = { ...filters.value, ...extra };
      const payload = await adminLeaveChartCache.get(params, {
        ttlMs: ADMIN_LEAVE_CHART_TTL,
        force: extra.force === true,
        loader: async () => {
          const { data } = await adminLeaveService.chart(params);
          return {
            year: data.year || chartYear.value,
            years:
              Array.isArray(data.years) && data.years.length
                ? data.years
                : [data.year || chartYear.value],
            categories: data.categories || chartCategories.value,
            series: data.series || [],
          };
        },
        onHydrate: (data) => {
          chartYear.value = data.year || chartYear.value;
          chartYears.value = data.years || [chartYear.value];
          chartCategories.value = data.categories || chartCategories.value;
          chartSeries.value = data.series || [];
        },
      });

      chartYear.value = payload.year || chartYear.value;
      chartYears.value = payload.years || [chartYear.value];
      chartCategories.value = payload.categories || chartCategories.value;
      chartSeries.value = payload.series || [];
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to load leave chart",
        "error"
      );
    }
  }

  // For downloads
  async function exportReport(format = "xlsx") {
    const params = { ...filters.value };
    return adminLeaveService.exportReport(format, params);
  }

  async function fetchAll(extra = {}) {
    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      const payload = await adminLeaveListCache.get(params, {
        ttlMs: ADMIN_LEAVE_LIST_TTL,
        force: extra.force === true,
        loader: async () => {
          const res = await adminLeaveService.list(params);
          const data = res.data;

          if (data?.data && (data?.meta || data?.total !== undefined)) {
            return {
              items: data.data,
              pagination: {
                total: data.meta?.total ?? data.total ?? data.data.length,
                per_page:
                  data.meta?.per_page ?? data.per_page ?? params.per_page ?? 25,
                current_page: data.meta?.current_page ?? data.current_page ?? 1,
              },
            };
          }

          const rows = Array.isArray(data) ? data : [];
          return {
            items: rows,
            pagination: {
              total: rows.length,
              per_page: params.per_page ?? 25,
              current_page: 1,
            },
          };
        },
        onHydrate: ({ items: rows, pagination: pageData }) => {
          items.value = rows || [];
          pagination.value = pageData || pagination.value;
        },
      });
      items.value = payload.items || [];
      pagination.value = payload.pagination || pagination.value;
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch leaves";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function fetchStats(extra = {}) {
    try {
      const params = { ...filters.value, ...extra };
      const payload = await adminLeaveStatsCache.get(params, {
        ttlMs: ADMIN_LEAVE_STATS_TTL,
        force: extra.force === true,
        loader: async () => {
          const { data } = await adminLeaveService.stats(params);
          return (
            data || {
              total_pending: 0,
              total_approved: 0,
              total_declined: 0,
              days_booked: 0,
            }
          );
        },
        onHydrate: (data) => {
          stats.value = data;
        },
      });

      stats.value = payload;
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to load stats", "error");
    }
  }

  async function ensureChart(extra = {}) {
    return await fetchChart({ ...extra, force: false });
  }

  async function ensureAll(extra = {}) {
    return await fetchAll({ ...extra, force: false });
  }

  async function ensureStats(extra = {}) {
    return await fetchStats({ ...extra, force: false });
  }

  function invalidateLeaveCaches() {
    adminLeaveListCache.clear();
    adminLeaveStatsCache.clear();
    adminLeaveChartCache.clear();
  }

  async function approve(id) {
    loading.value = true;
    try {
      const { data } = await adminLeaveService.approve(id);
      const idx = items.value.findIndex((r) => r.id === id);
      if (idx !== -1) items.value[idx] = data;
      invalidateLeaveCaches();
      await fetchStats();
      await fetchAll();
      toast("Leave approved");
    } catch (e) {
      toast(e?.response?.data?.message || "Approve failed", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function decline(id, reason = "") {
    loading.value = true;
    try {
      const { data } = await adminLeaveService.decline(id, { reason });
      const idx = items.value.findIndex((r) => r.id === id);
      if (idx !== -1) items.value[idx] = data;
      invalidateLeaveCaches();
      await Promise.all([fetchStats(), fetchAll()]);
      toast("Leave declined");
    } catch (e) {
      toast(e?.response?.data?.message || "Decline failed", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkApprove(ids) {
    try {
      const { data } = await adminLeaveService.bulkApprove(ids);
      invalidateLeaveCaches();
      await Promise.all([fetchAll(), fetchStats()]);
      const skipped = Number(data?.skipped_own || 0);
      toast(
        skipped > 0 ? data.message : "Selected leaves approved",
        skipped > 0 ? "warning" : "success"
      );
    } catch (e) {
      toast(e?.response?.data?.message || "Bulk approve failed", "error");
      throw e;
    }
  }

  async function bulkDecline(ids, reason = "") {
    await adminLeaveService.bulkDecline(ids, { reason });
    invalidateLeaveCaches();
    await Promise.all([fetchAll(), fetchStats()]);
    toast("Selected leaves declined");
  }

  async function revoke(id, reason = "") {
    loading.value = true;
    try {
      const { data } = await adminLeaveService.revoke(id, { reason });
      const idx = items.value.findIndex((r) => r.id === id);
      if (idx !== -1) items.value[idx] = data;
      invalidateLeaveCaches();
      await fetchStats();
      toast("Leave cancelled");
    } catch (e) {
      toast(e?.response?.data?.message || "Revoke failed", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    try {
      await adminLeaveService.create(payload);
      invalidateLeaveCaches();
      await Promise.all([fetchAll({ page: 1 }), fetchStats()]);
      toast(payload.auto_approve ? "Leave applied and approved" : "Leave applied for employee");
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to apply leave", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchEmployeeSummary(employeeId, year = new Date().getFullYear()) {
    const { data } = await adminLeaveService.employeeSummary({ employee_id: employeeId, year });
    return data?.types || [];
  }

  return {
    items,
    loading,
    error,
    pagination,
    totalPages,
    filters,
    stats,
    // chart:
    chartYear,
    chartYears,
    chartMonths,
    chartSeries,
    chartCategories,
    fetchChart,
    fetchAll,
    fetchStats,
    ensureChart,
    ensureAll,
    ensureStats,
    approve,
    decline,
    bulkApprove,
    bulkDecline,
    revoke,
    create,
    fetchEmployeeSummary,
    exportReport,
    invalidateLeaveCaches,
  };
});
